import {
	Box,
	CircularProgress,
	Divider,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';

interface StatisticsPanelProps {
	/**
	 * Year-based data: { '2023': 150, '2024': 200 }
	 */
	yearData: Record<string, number> | null | undefined;

	/**
	 * Whether data is loading
	 */
	isLoading?: boolean;

	/**
	 * Panel title
	 */
	title?: string;

	/**
	 * Label for the count column
	 */
	countLabel?: string;

	/**
	 * Label for the total row
	 */
	totalLabel?: string;

	/**
	 * Whether to show the divider at the top
	 */
	showDivider?: boolean;

	/**
	 * Extra columns to render
	 */
	extraColumns?: Array<{
		key: string;
		label: string;
		render: (year: string, count: number) => React.ReactNode;
	}>;
}

/**
 * Reusable statistics panel for displaying year-based data in dialogs.
 * Used by FacultyDialog, MajorDialog, AdmissionMethodDialog.
 *
 * @example
 * <StatisticsPanel
 *   yearData={statsData?.yearData}
 *   isLoading={isStatsLoading}
 *   title="Thống kê sinh viên theo năm"
 * />
 */
export function StatisticsPanel({
	yearData,
	isLoading = false,
	title = 'Thống kê sinh viên theo năm',
	countLabel = 'Số lượng',
	totalLabel = 'Tổng cộng',
	showDivider = true,
	extraColumns = [],
}: StatisticsPanelProps) {
	if (!yearData && !isLoading) {
		return null;
	}

	const sortedYears = yearData
		? Object.keys(yearData).sort((a, b) => parseInt(b) - parseInt(a))
		: [];

	const totalCount = yearData
		? Object.values(yearData).reduce((sum, count) => sum + count, 0)
		: 0;

	return (
		<Box sx={{ mt: 2 }}>
			{showDivider && <Divider sx={{ my: 2 }} />}

			<Typography variant="subtitle1" fontWeight="bold" gutterBottom>
				{title}
			</Typography>

			{isLoading ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
					<CircularProgress size={24} />
				</Box>
			) : sortedYears.length > 0 ? (
				<TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
					<Table size="small" stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell>Năm</TableCell>
								<TableCell align="right">{countLabel}</TableCell>
								{extraColumns.map((col) => (
									<TableCell key={col.key} align="right">
										{col.label}
									</TableCell>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							{sortedYears.map((year) => (
								<TableRow key={year}>
									<TableCell>{year}</TableCell>
									<TableCell align="right">
										{yearData![year].toLocaleString()}
									</TableCell>
									{extraColumns.map((col) => (
										<TableCell key={col.key} align="right">
											{col.render(year, yearData![year])}
										</TableCell>
									))}
								</TableRow>
							))}
							<TableRow sx={{ '& td': { fontWeight: 'bold' } }}>
								<TableCell>{totalLabel}</TableCell>
								<TableCell align="right">{totalCount.toLocaleString()}</TableCell>
								{extraColumns.map((col) => (
									<TableCell key={col.key} align="right">
										{/* Total for extra columns can be computed by parent if needed */}
									</TableCell>
								))}
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>
			) : (
				<Typography variant="body2" color="text.secondary">
					Chưa có dữ liệu thống kê
				</Typography>
			)}
		</Box>
	);
}

export default StatisticsPanel;
